import { useState } from "react";
import { toast } from "sonner";
import { Wand2 } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export const PILLARS = [
  ["health", "Health", "By the end of my year, my health and energy…"],
  ["body", "Body", "By the end of my year, my body…"],
  ["confidence", "Confidence", "By the end of my year, I feel…"],
  ["routines", "Routines", "By the end of my year, my days include…"],
  ["lifestyle", "Lifestyle", "By the end of my year, my life has…"],
];

export function VisionStudio({ vision, setVision, onShaped }) {
  const [ideas, setIdeas] = useState("");
  const [busy, setBusy] = useState(false);
  const [shaped, setShaped] = useState(null);
  const { user } = useAuth();

  const shape = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/v2/vision/shape", { ideas });
      setShaped(data);
      setVision({ ...vision, ...data.vision });
      onShaped?.(data);
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };

  return (
    <div className="space-y-5" data-testid="vision-studio">
      {!user?.ai_consent ? (
        <p className="card p-4 text-sm text-stone-600" data-testid="vision-ai-off">AI is off, so write it in your own words below. A few words in one area is enough. You can turn AI on later in the Privacy Center.</p>
      ) : <div className="card p-5">
        <label className="text-sm font-semibold">Tell me what you want from this year. Messy is perfect.</label>
        <textarea data-testid="vision-ideas" value={ideas} onChange={(e) => setIdeas(e.target.value)} rows={4} maxLength={3000}
          placeholder="e.g. lose the weight I've been carrying, sleep properly, feel confident at work, maybe learn to paint, a trip with my best friend…"
          className="mt-2 w-full rounded-xl border border-stone-200 p-3 text-[15px] focus:outline-none focus:border-stone-500" />
        <button onClick={shape} disabled={busy || ideas.trim().length < 5} data-testid="vision-shape-btn" className="btn-ink mt-3 px-5 h-11 rounded-full text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-40">
          <Wand2 className="h-4 w-4" />{busy ? "Imagining your year…" : "Shape my vision"}
        </button>
      </div>}
      {shaped && (
        <div className="card p-6 bg-[#2D2926] border-[#2D2926] text-stone-100 rise" data-testid="vision-shaped">
          <div className="eyebrow !text-stone-400">Your year, imagined</div>
          <h3 className="font-display text-2xl mt-2" data-testid="vision-headline">{shaped.headline}</h3>
          <p className="text-stone-300 mt-3 leading-relaxed text-[15px] italic">{shaped.story}</p>
          <div className="mt-5 eyebrow !text-stone-400">Possibilities you might not have considered</div>
          <ul className="mt-2 space-y-2.5">{(shaped.possibilities || []).map((p) => <li key={p.title} className="text-sm"><span className="font-semibold text-white">{p.title}.</span> <span className="text-stone-300">{p.text}</span></li>)}</ul>
          <div className="mt-5 eyebrow !text-stone-400">This week</div>
          <ul className="mt-2 space-y-1 text-sm text-stone-300 list-disc pl-5">{(shaped.first_steps || []).map((s) => <li key={s}>{s}</li>)}</ul>
        </div>
      )}
      <div className="space-y-3">
        {PILLARS.map(([k, l, p]) => (
          <label key={k} className="block">
            <span className="text-xs font-semibold uppercase tracking-wider text-stone-500">{l}</span>
            <textarea data-testid={`vision-${k}`} value={vision[k] || ""} onChange={(e) => setVision({ ...vision, [k]: e.target.value })} rows={2} placeholder={p}
              className="mt-1 w-full rounded-xl border border-stone-200 p-3 text-[15px] bg-white focus:outline-none focus:border-stone-500" />
          </label>
        ))}
      </div>
    </div>
  );
}

export function PillarSliders({ values, setValues }) {
  return (
    <div className="space-y-4" data-testid="pillar-sliders">
      {PILLARS.map(([k, l]) => (
        <div key={k}>
          <div className="flex justify-between text-sm"><span className="font-semibold">{l}</span><span className="text-stone-500">{values[k] || 5}/10</span></div>
          <input type="range" min={1} max={10} value={values[k] || 5} onChange={(e) => setValues({ ...values, [k]: Number(e.target.value) })}
            data-testid={`pillar-slider-${k}`} className="w-full accent-[#2D2926]" />
        </div>
      ))}
    </div>
  );
}
